import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, GitMerge } from 'lucide-react';

const ConfirmMergeModal = ({ isOpen, onClose, onConfirm, primaryName, sourceNames, loading = false }) => {
    const [confirmText, setConfirmText] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setConfirmText('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const isMergeEnabled = confirmText.toLowerCase() === 'merge';

    const handleConfirm = () => {
        if (isMergeEnabled) {
            onConfirm();
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={onClose}
                />

                <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Confirm Merge
                        </h3>
                    </div>

                    <div className="text-gray-600 mb-4 space-y-2">
                        <p>
                            You are about to merge the following client(s) into <strong>{primaryName}</strong>:
                        </p>
                        <ul className="list-disc list-inside text-sm">
                            {(sourceNames || []).map((name, idx) => (
                                <li key={idx}>{name}</li>
                            ))}
                        </ul>
                        <p className="text-sm text-red-600 font-medium">
                            This action cannot be undone. Source clients will be permanently deleted.
                        </p>
                    </div>

                    <div className="mb-6">
                        <label htmlFor="merge-confirm" className="block text-sm font-medium text-gray-700 mb-2">
                            Type "merge" to confirm:
                        </label>
                        <input
                            id="merge-confirm"
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder='Type "merge" here'
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                            autoComplete="off"
                        />
                    </div>

                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={loading || !isMergeEnabled}
                            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                                isMergeEnabled
                                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            } disabled:opacity-50`}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Merging...
                                </>
                            ) : (
                                <>
                                    <GitMerge className="w-4 h-4" />
                                    Execute Merge
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmMergeModal;
